import { ButtonProps } from '@/atoms';
import { type Color, colors, getHoverColor, isColorNameMappedToHex, nameToHex } from '@/styles';

export const resetButton = () => `
    padding: 0;
    border: none;
    outline: none;
    background-color: transparent;
  `;

type ButtonColorProps = {
  color?: ButtonProps['color'];
  borderColor?: ButtonProps['borderColor'];
  textColor?: ButtonProps['textColor'];
  hoverColor?: ButtonProps['hoverColor'];
  outline?: ButtonProps['outline'];
  disabled?: ButtonProps['disabled'];
};

/**
 * Add CSS for button colors.
 */
export function buttonColors(
  props: ButtonColorProps,
  defaultTextColor: Color = colors.white,
  defaultBorderColor: Color = colors.boulder,
) {
  let mainColor = props.color;
  let mainBorderColor = props.borderColor;
  let mainTextColor = props.textColor;
  if (mainColor && isColorNameMappedToHex(mainColor)) {
    mainColor = nameToHex(mainColor);
  }
  if (mainTextColor && isColorNameMappedToHex(mainTextColor)) {
    mainTextColor = nameToHex(mainTextColor);
  }
  if (mainBorderColor && isColorNameMappedToHex(mainBorderColor)) {
    mainBorderColor = nameToHex(mainBorderColor);
  }

  if (props.outline) {
    const getTextColor = (props.textColor !== defaultTextColor && mainTextColor) || mainColor;
    const getBorderColor =
      (props.borderColor !== defaultBorderColor && mainBorderColor) || mainColor;
    if (props.disabled) {
      return `
        border: 0.1rem solid ${colors.disableGrey};
        background: ${colors.white};
        color: ${colors.disableGrey};

        &:hover {
          color: ${colors.disableGrey};
        }
      `;
    }

    return `
        background: none;
        border: 0.1rem solid ${getBorderColor};
        color: ${getTextColor};
        &:hover:not(:disabled) {
          background: ${props.hoverColor ? props.hoverColor : colors.mist};
          border-color: ${colors.silver};
          color: ${colors.disableGrey};
        }

        &:disabled {
          border: 0.1rem solid ${colors.silverSand};
          color: ${colors.silverSand}
        }
        `;
  }

  if (props.disabled) {
    return `
      background: ${colors.whiteSmoke};
      border: 0;
      color: ${colors.silverSand};

      &:hover {
        color: ${colors.silverSand};
      }
      `;
  }

  return `
      background: ${mainColor};
      &:hover {
        color: ${mainTextColor};
        background: ${props.hoverColor ? props.hoverColor : getHoverColor(`${props.color}`)};
      }
      `;
}

type ButtonSizeOptions = {
  /** Use `min-height` for buttons instead of `height` */
  useMinHeight?: boolean;
};

/**
 * Add button size CSS
 */
export function buttonSize(
  { size }: { size: ButtonProps['size'] },
  { useMinHeight = false }: ButtonSizeOptions = {},
) {
  let height;
  let fontSize;
  let padding;
  switch (size) {
    case 'xsmall':
      height = '1.6rem';
      fontSize = '0.675rem';
      padding = '0 1rem';
      break;
    case 'small':
      height = '1.8rem';
      fontSize = '0.775rem';
      padding = '0 1.2rem';
      break;
    case 'medium':
      height = '2rem';
      fontSize = '0.875rem';
      padding = '0 1.4rem';
      break;
    case 'large':
      height = '2.2rem';
      fontSize = '1rem';
      padding = '0 1.6rem';
      break;
    case 'xlarge':
      height = '2.5rem';
      fontSize = '1.125rem';
      padding = '0 1.8rem';
      break;
  }
  return `
      ${useMinHeight ? 'min-height' : 'height'}: ${height};
      font-size: ${fontSize};
      padding: ${padding};
      `;
}
